/**
 * Filter-based ("select all in set") bulk-operation jobs — hand-written against
 * client.instance like the other /contacts/_bulk/* mutations. Backed by
 * POST/GET /contacts/_bulk/jobs (datahub-api ContactBulkJobEndpoints). Live
 * progress arrives over the SignalR jobs hub (src/realtime); the GET endpoints
 * are the seed + polling fallback.
 */

import { useMutation } from '@tanstack/react-query';

import { useBulkJobsStore, type BulkJobStatus } from '@/realtime/bulkJobsStore';

import { client } from '../generated/client.gen';
import type { BulkEditFields } from './contacts';
import type { ConditionTree } from './workspace';

export interface BulkJobDto {
  id: string;
  operation: string;
  status: BulkJobStatus;
  total: number;
  processed: number;
  affected: number;
  skipped: number;
  errors: { id: string; error: string }[];
  error?: string | null;
  createdAt: string;
  startedAt?: string | null;
  finishedAt?: string | null;
}

export interface StartBulkJobInput {
  operation: 'review' | 'status' | 'delete' | 'edit';
  /** Free-text quick search, same semantics as the contacts list. */
  query?: string;
  /** The grid's condition tree, same model as POST /contacts/_search. */
  conditions?: ConditionTree;
  // review params
  status?: 'none' | 'requiresReview' | 'reviewed' | 'autoInvestigation' | 'investigated';
  mode?: 'manual' | 'automatic';
  // status (activate/deactivate) param
  active?: boolean;
  // edit param — the field changes applied to every matching contact.
  fields?: BulkEditFields;
}

function useStartBulkJob() {
  const upsert = useBulkJobsStore((s) => s.upsert);
  return useMutation({
    mutationFn: async (input: StartBulkJobInput): Promise<BulkJobDto> => {
      const res = await client.instance.post<BulkJobDto>('/contacts/_bulk/jobs', input);
      return res.data;
    },
    onSuccess: (job) => {
      // Seed the store so the progress dialog has data before the first
      // SignalR push arrives.
      upsert({
        id: job.id,
        operation: job.operation,
        status: job.status,
        total: job.total,
        processed: job.processed,
        affected: job.affected,
        skipped: job.skipped,
        error: job.error,
      });
    },
  });
}

export async function fetchRecentBulkJobs(limit = 10): Promise<BulkJobDto[]> {
  const res = await client.instance.get<BulkJobDto[]>('/contacts/_bulk/jobs', {
    params: { limit },
  });
  return res.data;
}

export const bulkJobsApi = {
  useStartBulkJob,
};
